// ReportInsightCard — drop-in AI analysis card for Calories Reports
// (macros / nutrients / junk). Same UX spec as Body Composition + Readiness:
// AIAnalysingPipeline while loading, AIBulletText (OUTPUT_BULLETS_BOLD) on
// result, per-key localStorage cache so flipping report tabs is free.
//
// Each call site passes:
//   - `kind`      : "macros" | "nutrients" | "junk"  (cache + feature key)
//   - `rangeDays` : 7 | 14 | 30 | 60 | 90  (cache key namespacing)
//   - `snapshot`  : the structured numbers we send to Gemini
//   - `task`      : the one-line task instruction tail for the system prompt
//   - `sources`   : ordered label array driving the pipeline rows
//   - `colors`    : palette pulled from CalorieCounter (text/muted/border/...)

import React, { useEffect, useState } from "react";
import { getGeminiKey, callGemini } from "../geminiAI";
import { COACH_VOICE, EVIDENCE_STANDARD, OUTPUT_BULLETS_BOLD, composeSystem } from "./hayahPrompts";
import { getUserProfile, formatProfileForPrompt } from "./userProfile";
import { MODELS } from "./aiConfig";
import { publishSources } from "./pipelineSources";
import AIAnalysingPipeline from "./AIAnalysingPipeline";
import AIBulletText from "./AIBulletText";
import AICostBadge from "./AICostBadge";
import { runJob, subscribe, isInFlight, lastError } from "./aiBackgroundJob";

const FEATURE_PREFIX = "report-";
const CACHE_PREFIX   = "hayahReportInsight_";

const KIND_LABEL = {
  macros:    "Macro analysis",
  nutrients: "Micronutrient analysis",
  junk:      "Junk-food analysis",
};
const COST_FEATURE_KEY = "reportInsight";

export default function ReportInsightCard({
  kind,
  rangeDays,
  snapshot,
  task,
  sources,
  colors,
}) {
  const { text, muted, border, teal, danger } = colors;
  const cacheKey = `${CACHE_PREFIX}${kind}_${rangeDays}`;
  const feature  = `${FEATURE_PREFIX}${kind}`;

  const [analysis, setAnalysis] = useState(() => {
    try { return JSON.parse(localStorage.getItem(cacheKey) || "null"); } catch { return null; }
  });
  const [loading, setLoading] = useState(() => isInFlight(cacheKey));
  const [error, setError]     = useState(() => lastError(cacheKey));

  // Subscribe to module-level job state for this key so a generate that
  // was started here and finished while the user was on another tab still
  // surfaces the moment they come back. The subscribe callback fires
  // once immediately at registration, closing the read-then-subscribe
  // race that otherwise left the UI showing "Analyse" after returning
  // mid-flight.
  useEffect(() => {
    const sync = () => {
      setLoading(isInFlight(cacheKey));
      setError(lastError(cacheKey));
      if (!isInFlight(cacheKey)) {
        try { setAnalysis(JSON.parse(localStorage.getItem(cacheKey) || "null")); }
        catch {}
      }
    };
    return subscribe(cacheKey, sync);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  const run = () => {
    if (loading) return;
    if (!getGeminiKey()) {
      setError("Add a Gemini API key in Hayah Settings → AI Assistant.");
      return;
    }
    // Snapshot the inputs so the background job is self-contained — the
    // owning component might unmount before the call returns.
    const fixedSnapshot = snapshot;
    const fixedSources  = sources;
    const fixedTask     = task;
    const fixedKind     = kind;
    const fixedRange    = rangeDays;
    runJob(cacheKey, async () => {
      const profile = getUserProfile();
      const sys = composeSystem([
        COACH_VOICE,
        formatProfileForPrompt(profile),
        EVIDENCE_STANDARD,
        OUTPUT_BULLETS_BOLD,
        `Task: ${fixedTask}`,
      ]);
      try { publishSources(feature, fixedSources); } catch {}
      const out = await callGemini(
        `${KIND_LABEL[fixedKind] || "Report analysis"} · ${fixedRange}-day window:\n\n${JSON.stringify(fixedSnapshot, null, 2)}`,
        {
          systemInstruction: sys,
          model: MODELS.todayInsights || "gemini-2.5-flash",
          maxOutputTokens: 4096,
          feature: COST_FEATURE_KEY,
        },
      );
      const result = { text: out, ts: Date.now() };
      try { localStorage.setItem(cacheKey, JSON.stringify(result)); } catch {}
    });
  };

  return (
    <div style={{
      margin: "12px 16px 0",
      padding: "12px 14px",
      background: `${teal}0d`,
      border: `1px solid ${teal}33`,
      borderRadius: 12,
    }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: (analysis || loading || error) ? 10 : 0,
      }}>
        <div style={{
          fontSize: 10, color: teal, fontFamily: "monospace", fontWeight: 700,
          letterSpacing: "0.14em", display: "inline-flex", alignItems: "baseline", gap: 8,
        }}>
          <span>✦ AI ANALYSIS · {rangeDays}D</span>
          {analysis?.ts && (
            <span style={{ color: muted, fontWeight: 600, letterSpacing: "0.04em" }}>
              {new Date(analysis.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
          <AICostBadge feature={COST_FEATURE_KEY} prefix="·" fontSize={9} color={muted} />
        </div>
        <button
          onClick={run}
          disabled={loading}
          style={{
            padding: "5px 11px", borderRadius: 8,
            border: `1px solid ${teal}55`,
            background: loading ? "transparent" : `${teal}18`,
            color: teal, fontFamily: "monospace", fontSize: 10, fontWeight: 800,
            letterSpacing: "0.08em", cursor: "pointer", opacity: loading ? 0.55 : 1,
          }}
        >{loading ? "THINKING…" : analysis ? "↺ REFRESH" : "✦ ANALYSE"}</button>
      </div>

      {loading && (
        <AIAnalysingPipeline
          label={KIND_LABEL[kind] || "Analysing"}
          computeLabel="Reading window"
          feature={feature}
          accent="teal"
          theme={{ teal, amber: teal, ink3: muted, ink4: muted, lineSoft: border, line: border }}
        />
      )}

      {!loading && analysis?.text && (
        <AIBulletText text={analysis.text} accent={teal} textColor={text} fontSize={12.5} />
      )}

      {!loading && error && !analysis?.text && (
        <div style={{ fontSize: 11, color: danger, lineHeight: 1.55 }}>{error}</div>
      )}

      {!analysis && !loading && !error && (
        <div style={{ fontSize: 11.5, color: muted, lineHeight: 1.55 }}>
          Tap to ask Gemini what this {rangeDays}-day window is telling you — and what to adjust next.
        </div>
      )}
    </div>
  );
}
